import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adapter } from '../providers/adapter';
import { authService } from '../api/auth.service';
import type { SignInCredentials, OAuthProvider } from '../types';
import { useAuthStore } from '@/core/stores/auth.store';
import { usePostLoginRedirect } from '@/core/hooks/usePostLoginRedirect';

export function useLogin() {
  const { t } = useTranslation('auth');
  const setSession = useAuthStore((s) => s.setSession);
  const setTokens = useAuthStore((s) => s.setTokens);
  const loginWithProvider = useAuthStore((s) => s.loginWithProvider);
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  usePostLoginRedirect();

  const loginWithPassword = async (credentials: SignInCredentials): Promise<void> => {
    setIsPending(true);
    setIsError(false);
    setError(null);
    try {
      const tokens = await adapter.signInWithPassword(credentials);
      // Store the token before getProfile so the httpClient injects it on that
      // bootstrap request — otherwise it goes out unauthenticated and 401s.
      setTokens(tokens);
      const user = await authService.getProfile();
      setSession(user, tokens);
    } catch (err) {
      if (err instanceof Error && err.message === 'account_inactive') {
        await adapter.logout().catch(() => {});
        setIsError(true);
        setError(new Error(t('errors.accountInactive')));
      } else {
        setIsError(true);
        setError(err instanceof Error ? err : new Error(t('errors.loginFailed')));
      }
    }
    setIsPending(false);
  };

  const handleLoginWithProvider = async (provider: OAuthProvider): Promise<void> => {
    setIsPending(true);
    try {
      await loginWithProvider(provider);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error(t('errors.loginFailed')));
    }
    setIsPending(false);
  };

  return {
    loginWithPassword,
    loginWithProvider: handleLoginWithProvider,
    isPending,
    isError,
    error,
  };
}
